import { FormEvent, useId, useState } from "react"
import { Meeting } from "@/schema"
import { Input, Label } from "@ui/shad"
import { AudioLines, TriangleAlert } from "lucide-react"
import { toast } from "sonner"

import { useCreateRecordingTranscription } from "@/hooks/api/meetings/mutations"
import { SimpleFormDialog } from "@/components/dialogs"

const MIN_SPEAKERS = 2
const MAX_SPEAKERS = 30

interface MultipleSpeakerReprocessingDialogProps {
  meeting: Meeting
  open: boolean
  onOpenChange: (open: boolean) => void
}

export default function MultipleSpeakerReprocessingDialog({
  meeting,
  open,
  onOpenChange,
}: MultipleSpeakerReprocessingDialogProps) {
  const dialogFormId = useId()

  const [speakerCount, setSpeakerCount] = useState(MIN_SPEAKERS)
  const [speakerNames, setSpeakerNames] = useState<string[]>(Array(MIN_SPEAKERS).fill(""))
  const [validationMessage, setValidationMessage] = useState<string>()
  const candidateListId = useId()
  const transcriptionMutation = useCreateRecordingTranscription()

  function resetForm() {
    setSpeakerCount(MIN_SPEAKERS)
    setSpeakerNames(Array(MIN_SPEAKERS).fill(""))
    setValidationMessage(undefined)
  }

  function handleOpenChange(nextOpen: boolean) {
    if (transcriptionMutation.isPending) return
    if (!nextOpen) resetForm()
    onOpenChange(nextOpen)
  }

  function handleSpeakerCountChange(value: string) {
    const parsedCount = Number.parseInt(value, 10)
    if (Number.isNaN(parsedCount)) return

    const nextCount = Math.min(MAX_SPEAKERS, Math.max(MIN_SPEAKERS, parsedCount))
    setSpeakerCount(nextCount)
    setSpeakerNames((currentNames) => Array.from({ length: nextCount }, (_, index) => currentNames[index] ?? ""))
    setValidationMessage(undefined)
  }

  function handleSpeakerNameChange(index: number, value: string) {
    setSpeakerNames((currentNames) =>
      currentNames.map((currentName, currentIndex) => (currentIndex === index ? value : currentName))
    )
    setValidationMessage(undefined)
  }

  function validateSpeakerNames() {
    const normalizedNames = speakerNames.map((name) => name.trim())
    if (normalizedNames.some((name) => !name)) return "Enter a name for every speaker."

    const uniqueNames = new Set(normalizedNames.map((name) => name.toLowerCase()))
    if (uniqueNames.size !== normalizedNames.length) return "Each speaker needs a unique name."

    return undefined
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()

    const error = validateSpeakerNames()
    if (error) {
      setValidationMessage(error)
      return
    }

    try {
      await transcriptionMutation.mutateAsync({
        meetingId: meeting.id,
        speakers: speakerNames.map((name) => name.trim()),
      })
    } catch {
      return
    }

    toast.success("Reprocessing transcript for multiple speakers")
    resetForm()
    onOpenChange(false)
  }

  return (
    <SimpleFormDialog
      title={"Separate voices from one device"}
      open={open}
      onOpenChange={handleOpenChange}
      action={{
        label: (
          <>
            <AudioLines />
            {transcriptionMutation.isPending ? "Starting..." : "Reprocess transcript"}
          </>
        ),
        form: `${dialogFormId}-dialog-multiple-speaker-reprocessing-dialog-102`,
        disabled: transcriptionMutation.isPending,
      }}
      cancel={{ label: "Cancel", onClick: () => handleOpenChange(false), disabled: transcriptionMutation.isPending }}
    >
      <form id={`${dialogFormId}-dialog-multiple-speaker-reprocessing-dialog-102`} onSubmit={handleSubmit}>
        <div className="space-y-5">
          <div className="text-muted-foreground text-sm">Enter everyone heard in the recording.</div>

          <div className="border-yellow-border bg-yellow-background text-yellow-foreground flex gap-3 rounded-md border p-3">
            <TriangleAlert className="text-yellow-accent mt-0.5 size-4 shrink-0" />
            <p className="text-sm">
              Use the exact count. This replaces the transcript and meeting materials and can only be done once.
            </p>
          </div>

          <div className="flex items-center justify-between gap-4">
            <Label htmlFor="speaker-count">Number of people</Label>
            <Input
              id="speaker-count"
              type="number"
              min={MIN_SPEAKERS}
              max={MAX_SPEAKERS}
              value={speakerCount}
              onChange={(event) => handleSpeakerCountChange(event.target.value)}
              className="w-20"
              disabled={transcriptionMutation.isPending}
            />
          </div>

          <div className="space-y-3">
            <div className="flex items-baseline justify-between gap-4">
              <Label>Names</Label>
              <p className="text-muted-foreground text-xs">Order doesn&apos;t matter. Names can be corrected later.</p>
            </div>
            {speakerNames.map((name, index) => (
              <div key={index}>
                <Label className="sr-only" htmlFor={`speaker-${index}`}>
                  Person {index + 1} name
                </Label>
                <Input
                  id={`speaker-${index}`}
                  list={candidateListId}
                  value={name}
                  onChange={(event) => handleSpeakerNameChange(index, event.target.value)}
                  placeholder="Search or enter a name"
                  autoComplete="off"
                  aria-invalid={validationMessage ? !name.trim() : undefined}
                  disabled={transcriptionMutation.isPending}
                />
              </div>
            ))}
            <datalist id={candidateListId}>
              {meeting.speaker_candidates?.map((candidate) => (
                <option key={candidate.name} value={candidate.name} label={candidate.description} />
              ))}
            </datalist>
            {validationMessage && <p className="text-red-accent text-sm">{validationMessage}</p>}
          </div>
        </div>
      </form>
    </SimpleFormDialog>
  )
}
